import {useEffect, useState, useCallback, useRef, useId} from 'react'
import {InputRange} from 'dom-input-range'
import {ActionList, VisuallyHidden} from '@primer/react'
import {useOverlayDirection} from '@github-ui/copilot-chat-contexts/OverlayDirectionContext'
import {useResizeObserver} from '@github-ui/use-resize-observer'
import {clsx} from 'clsx'
import styles from './SlashCommandMenu.module.css'

const FULLY_VISIBLE_COMMANDS = 9

function scrollActiveOptionIntoView(list: Element) {
  list.querySelector<HTMLElement>('[aria-selected="true"]')?.scrollIntoView?.({block: 'nearest', inline: 'nearest'})
}

function updateMenuHeight(list: Element, keepActiveOptionVisible = false) {
  const dropdown = list.parentElement
  if (!dropdown) return

  const options = list.querySelectorAll('[role="option"]')
  const tenthOption = options[FULLY_VISIBLE_COMMANDS]
  let height = ''

  if (tenthOption) {
    const listTop = list.getBoundingClientRect().top
    const tenthRect = tenthOption.getBoundingClientRect()
    height = `${tenthRect.top - listTop + tenthRect.height / 2}px`
  }

  if (dropdown.style.getPropertyValue('--dropdown-row-capped-height') !== height) {
    if (height) dropdown.style.setProperty('--dropdown-row-capped-height', height)
    else dropdown.style.removeProperty('--dropdown-row-capped-height')
  }

  if (keepActiveOptionVisible) scrollActiveOptionIntoView(list)
}

function restoreAttr(el: HTMLElement, name: string, value: string | null) {
  if (value === null) el.removeAttribute(name)
  else el.setAttribute(name, value)
}

interface CaretPosition {
  top: number
  bottom: number
  left: number
  inputTop: number
  inputBottom: number
  inputLeft: number
  inputWidth: number
  inputTextTop: number
  inputTextLeft: number
  inputTextWidth: number
  font: string
  lineHeight: string
  letterSpacing: string
  wordSpacing: string
  viewportWidth: number
  viewportHeight: number
}

function getCaretPosition(textarea: HTMLTextAreaElement, currentText: string): CaretPosition | null {
  const textareaRect = textarea.getBoundingClientRect()
  if (textarea.getClientRects().length === 0 || textareaRect.width === 0 || textareaRect.height === 0) return null
  const style = getComputedStyle(textarea)
  const borderTopWidth = Number.parseFloat(style.borderTopWidth)
  const borderLeftWidth = Number.parseFloat(style.borderLeftWidth)
  const paddingTop = Number.parseFloat(style.paddingTop)
  const paddingLeft = Number.parseFloat(style.paddingLeft)
  const paddingRight = Number.parseFloat(style.paddingRight)
  const inputTextTop = textareaRect.top + borderTopWidth + paddingTop - textarea.scrollTop
  const inputTextLeft = textareaRect.left + borderLeftWidth + paddingLeft - textarea.scrollLeft
  const inputTextWidth = textarea.clientWidth - paddingLeft - paddingRight

  try {
    const rangeRect = new InputRange(textarea, currentText.length, currentText.length).getBoundingClientRect()
    const isWithinTextarea =
      rangeRect.left >= textareaRect.left - 1 &&
      rangeRect.left <= textareaRect.right + 1 &&
      rangeRect.top >= textareaRect.top - 1 &&
      rangeRect.bottom <= textareaRect.bottom + 1

    if (isWithinTextarea) {
      return {
        top: rangeRect.top,
        bottom: rangeRect.bottom,
        left: rangeRect.left,
        inputTop: textareaRect.top,
        inputBottom: textareaRect.bottom,
        inputLeft: textareaRect.left,
        inputWidth: textareaRect.width,
        inputTextTop,
        inputTextLeft,
        inputTextWidth,
        font: style.font,
        lineHeight: style.lineHeight,
        letterSpacing: style.letterSpacing,
        wordSpacing: style.wordSpacing,
        viewportWidth: window.innerWidth,
        viewportHeight: window.innerHeight,
      }
    }
  } catch {
    // Fall back to the textarea position below when caret measurement is unavailable.
  }

  return {
    top: textareaRect.top + 24,
    bottom: textareaRect.top + 24,
    left: textareaRect.left + 8,
    inputTop: textareaRect.top,
    inputBottom: textareaRect.bottom,
    inputLeft: textareaRect.left,
    inputWidth: textareaRect.width,
    inputTextTop,
    inputTextLeft,
    inputTextWidth,
    font: style.font,
    lineHeight: style.lineHeight,
    letterSpacing: style.letterSpacing,
    wordSpacing: style.wordSpacing,
    viewportWidth: window.innerWidth,
    viewportHeight: window.innerHeight,
  }
}

export interface SlashCommandSuggestion {
  key: string
  displayKey?: string
  description: string
  appendSpace?: boolean
  /** Placeholder for the command's argument, rendered muted after the key. */
  argumentHint?: string
  /** Marks the option whose value is already in effect. */
  current?: boolean
}

interface SlashCommandMenuProps {
  suggestions: SlashCommandSuggestion[]
  inlineHint?: string
  onSelect: (command: string) => void
  onDismiss?: (currentText: string) => void
  textAreaRef: React.RefObject<HTMLTextAreaElement | null>
  currentText: string
  /** Accessible name for the listbox. */
  label?: string
  /** Namespaces the listbox and option ids so several menus can coexist on a page. */
  idPrefix?: string
}

/**
 * Dropdown that shows matching slash commands as the user types.
 * Positioned next to the caret using dom-input-range.
 * Supports keyboard navigation and Tab completion.
 *
 * A single menu owns the textarea while it is open: it takes over the combobox
 * ARIA attributes and installs a keydown listener. Never render two of these
 * against the same textarea -- pass one merged suggestion list instead, or the
 * two menus will both handle ArrowDown and Enter.
 */
export function SlashCommandMenu({
  suggestions,
  inlineHint,
  onSelect,
  onDismiss,
  textAreaRef,
  currentText,
  label = 'Command suggestions',
  idPrefix = 'slash-command',
}: SlashCommandMenuProps) {
  const [caretPos, setCaretPos] = useState<CaretPosition | null>(null)
  const positionKey = `${currentText}\u0000${inlineHint ?? ''}\u0000${suggestions.map(command => command.key).join('\u0000')}`
  const [positionedKey, setPositionedKey] = useState<string | null>(null)
  const [activeIndex, setActiveIndex] = useState(0)
  const [hasNavigated, setHasNavigated] = useState(false)
  const observeList = useResizeObserver(entry => updateMenuHeight(entry.target, hasNavigated))
  const setListRef = useCallback(
    (list: HTMLUListElement | null) => {
      if (!list) return
      updateMenuHeight(list)
      return observeList(list)
    },
    [observeList],
  )
  const instanceId = useId()
  const listboxId = `${idPrefix}-autocomplete-listbox-${instanceId}`
  const hintId = `${idPrefix}-hint-${instanceId}`
  const dropdownRef = useRef<HTMLDivElement | null>(null)
  const hasSuggestions = suggestions.length > 0
  const typedCommand = currentText.trimStart()
  const preferredOverlaySide = useOverlayDirection()
  const prefersAbove = preferredOverlaySide === 'outside-top' || preferredOverlaySide === 'inside-top'

  // Avoid painting suggestions at the viewport origin before the first successful
  // measurement. Later updates keep the previous position while the next one is measured.
  const clampedIndex = suggestions.length > 0 ? activeIndex % suggestions.length : 0
  const activeSuggestion = suggestions[clampedIndex]
  const commandCompletion =
    activeSuggestion?.key.startsWith(typedCommand) && activeSuggestion.key !== typedCommand
      ? activeSuggestion.key.slice(typedCommand.length)
      : undefined
  const ghostText = inlineHint ?? commandCompletion
  const hasOverlay = hasSuggestions || Boolean(ghostText)
  const isPositioned = hasOverlay && caretPos !== null && positionedKey !== null

  const clampedIndexRef = useRef(clampedIndex)

  // Keep the active option ref in sync and expose the combobox relationship to screen readers.
  useEffect(() => {
    clampedIndexRef.current = clampedIndex
    const textarea = textAreaRef.current
    if (!textarea || !isPositioned || !hasSuggestions) return

    const activeOptionId = suggestions[clampedIndex] ? `${idPrefix}-option-${instanceId}-${clampedIndex}` : ''
    const prev = {
      role: textarea.getAttribute('role'),
      expanded: textarea.getAttribute('aria-expanded'),
      autocomplete: textarea.getAttribute('aria-autocomplete'),
      haspopup: textarea.getAttribute('aria-haspopup'),
      controls: textarea.getAttribute('aria-controls'),
      active: textarea.getAttribute('aria-activedescendant'),
    }
    textarea.setAttribute('role', 'combobox')
    textarea.setAttribute('aria-expanded', 'true')
    textarea.setAttribute('aria-autocomplete', 'list')
    textarea.setAttribute('aria-haspopup', 'listbox')
    textarea.setAttribute('aria-controls', listboxId)
    textarea.setAttribute('aria-activedescendant', activeOptionId)

    return () => {
      restoreAttr(textarea, 'role', prev.role)
      restoreAttr(textarea, 'aria-expanded', prev.expanded)
      restoreAttr(textarea, 'aria-autocomplete', prev.autocomplete)
      restoreAttr(textarea, 'aria-haspopup', prev.haspopup)
      restoreAttr(textarea, 'aria-controls', prev.controls)
      restoreAttr(textarea, 'aria-activedescendant', prev.active)
    }
  }, [suggestions, clampedIndex, isPositioned, hasSuggestions, textAreaRef, instanceId, listboxId, idPrefix])

  useEffect(() => {
    const textarea = textAreaRef.current
    if (!textarea || !isPositioned || !inlineHint) return

    const previous = textarea.getAttribute('aria-describedby')
    const ids = new Set(previous?.split(/\s+/).filter(Boolean))
    ids.add(hintId)
    textarea.setAttribute('aria-describedby', [...ids].join(' '))

    return () => restoreAttr(textarea, 'aria-describedby', previous)
  }, [hintId, inlineHint, isPositioned, textAreaRef])

  // Reset navigation state when suggestions change
  const [prevSuggestionsLength, setPrevSuggestionsLength] = useState(suggestions.length)
  if (prevSuggestionsLength !== suggestions.length) {
    setPrevSuggestionsLength(suggestions.length)
    setHasNavigated(false)
    setActiveIndex(0)
  }

  useEffect(() => {
    if (!hasOverlay) return

    let firstFrame: number | null = null
    let secondFrame: number | null = null
    let retryFrame: number | null = null
    let retryCount = 0

    const measure = () => {
      const textarea = textAreaRef.current
      const nextCaretPos = textarea ? getCaretPosition(textarea, currentText) : null
      const list = dropdownRef.current?.firstElementChild
      if (list) updateMenuHeight(list, hasNavigated)

      if (!nextCaretPos) {
        setPositionedKey(previousKey => (previousKey === positionKey ? null : previousKey))
        if (retryCount < 5) {
          retryCount += 1
          retryFrame = requestAnimationFrame(measure)
        }
        return
      }

      setPositionedKey(previousKey => (previousKey === positionKey ? previousKey : positionKey))
      setCaretPos(previousPosition => {
        if (
          previousPosition?.top === nextCaretPos.top &&
          previousPosition.bottom === nextCaretPos.bottom &&
          previousPosition.left === nextCaretPos.left &&
          previousPosition.inputTop === nextCaretPos.inputTop &&
          previousPosition.inputBottom === nextCaretPos.inputBottom &&
          previousPosition.inputLeft === nextCaretPos.inputLeft &&
          previousPosition.inputWidth === nextCaretPos.inputWidth &&
          previousPosition.inputTextTop === nextCaretPos.inputTextTop &&
          previousPosition.inputTextLeft === nextCaretPos.inputTextLeft &&
          previousPosition.inputTextWidth === nextCaretPos.inputTextWidth &&
          previousPosition.font === nextCaretPos.font &&
          previousPosition.lineHeight === nextCaretPos.lineHeight &&
          previousPosition.letterSpacing === nextCaretPos.letterSpacing &&
          previousPosition.wordSpacing === nextCaretPos.wordSpacing &&
          previousPosition.viewportWidth === nextCaretPos.viewportWidth &&
          previousPosition.viewportHeight === nextCaretPos.viewportHeight
        ) {
          return previousPosition
        }
        return nextCaretPos
      })
    }

    const scheduleMeasure = () => {
      if (firstFrame !== null) cancelAnimationFrame(firstFrame)
      if (secondFrame !== null) cancelAnimationFrame(secondFrame)
      if (retryFrame !== null) cancelAnimationFrame(retryFrame)
      retryCount = 0

      // Wait for the main menu portal and its positioning to settle.
      firstFrame = requestAnimationFrame(() => {
        secondFrame = requestAnimationFrame(measure)
      })
    }

    const textarea = textAreaRef.current
    const styleClone = textarea ? new InputRange(textarea).getStyleClone() : null
    const cloneContainer = styleClone?.element.parentElement
    if (cloneContainer) {
      // The mirror needs its child's layout for caret measurement, but its wrapper must not enlarge a scrollable ancestor.
      cloneContainer.style.width = '0'
      cloneContainer.style.height = '0'
      cloneContainer.style.overflow = 'clip'
    }
    styleClone?.forceUpdate()
    styleClone?.addEventListener('update', scheduleMeasure)
    scheduleMeasure()

    return () => {
      if (firstFrame !== null) cancelAnimationFrame(firstFrame)
      if (secondFrame !== null) cancelAnimationFrame(secondFrame)
      if (retryFrame !== null) cancelAnimationFrame(retryFrame)
      styleClone?.removeEventListener('update', scheduleMeasure)
      styleClone?.disconnect()
    }
  })

  useEffect(() => {
    if (!isPositioned) return
    const frame = requestAnimationFrame(() => {
      const textarea = textAreaRef.current
      if (!textarea) return
      const textareaRect = textarea.getBoundingClientRect()
      const style = getComputedStyle(textarea)
      const inputTextTop =
        textareaRect.top +
        Number.parseFloat(style.borderTopWidth) +
        Number.parseFloat(style.paddingTop) -
        textarea.scrollTop
      const inputTextLeft =
        textareaRect.left +
        Number.parseFloat(style.borderLeftWidth) +
        Number.parseFloat(style.paddingLeft) -
        textarea.scrollLeft
      const inputTextWidth =
        textarea.clientWidth - Number.parseFloat(style.paddingLeft) - Number.parseFloat(style.paddingRight)
      setCaretPos(position =>
        position &&
        (position.inputTop !== textareaRect.top ||
          position.inputBottom !== textareaRect.bottom ||
          position.inputLeft !== textareaRect.left ||
          position.inputWidth !== textareaRect.width ||
          position.inputTextTop !== inputTextTop ||
          position.inputTextLeft !== inputTextLeft ||
          position.inputTextWidth !== inputTextWidth)
          ? {
              ...position,
              inputTop: textareaRect.top,
              inputBottom: textareaRect.bottom,
              inputLeft: textareaRect.left,
              inputWidth: textareaRect.width,
              inputTextTop,
              inputTextLeft,
              inputTextWidth,
            }
          : position,
      )
    })
    return () => cancelAnimationFrame(frame)
  }, [currentText, isPositioned, textAreaRef])

  const handleSelect = useCallback(
    (command: SlashCommandSuggestion) => {
      const nextText = command.appendSpace || command.argumentHint ? `${command.key} ` : command.key
      onSelect(nextText)
      requestAnimationFrame(() => {
        const textarea = textAreaRef.current
        textarea?.focus()
        textarea?.setSelectionRange(nextText.length, nextText.length)
      })
    },
    [onSelect, textAreaRef],
  )

  // Keyboard navigation
  useEffect(() => {
    if (!isPositioned) return

    const handleClick = (event: MouseEvent) => {
      const target = event.target
      if (!(target instanceof Node)) return
      if (textAreaRef.current?.contains(target) || dropdownRef.current?.contains(target)) return
      onDismiss?.(currentText)
    }

    document.addEventListener('click', handleClick, true)
    return () => document.removeEventListener('click', handleClick, true)
  }, [currentText, isPositioned, onDismiss, textAreaRef])

  useEffect(() => {
    if (!isPositioned || !hasSuggestions) return
    const textarea = textAreaRef.current
    if (!textarea) return

    const handleKeyDown = (e: KeyboardEvent) => {
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          setHasNavigated(true)
          setActiveIndex(i => (i + 1) % suggestions.length)
          break
        case 'ArrowUp':
          e.preventDefault()
          setHasNavigated(true)
          setActiveIndex(i => (i - 1 + suggestions.length) % suggestions.length)
          break
        case 'Enter': {
          e.preventDefault()
          e.stopPropagation()
          const cmd = suggestions[clampedIndexRef.current]
          if (cmd) handleSelect(cmd)
          break
        }
        case 'Tab': {
          e.preventDefault()
          e.stopPropagation()
          const cmd = suggestions[clampedIndexRef.current]
          if (cmd) handleSelect(cmd)
          break
        }
        case 'Escape':
          e.preventDefault()
          e.stopPropagation()
          onDismiss?.(currentText)
          break
      }
    }

    textarea.addEventListener('keydown', handleKeyDown)
    return () => textarea.removeEventListener('keydown', handleKeyDown)
  }, [suggestions, handleSelect, isPositioned, hasSuggestions, textAreaRef, hasNavigated, currentText, onDismiss])

  useEffect(() => {
    if (!isPositioned) return
    if (dropdownRef.current) scrollActiveOptionIntoView(dropdownRef.current)
  }, [clampedIndex, isPositioned, positionedKey])

  if (!isPositioned) return null

  const viewportPadding = 16
  const dropdownOffset = 8
  const minimumVisibleMenuHeight = 180
  const availableAbove = caretPos.inputTop - viewportPadding
  const availableBelow = caretPos.viewportHeight - caretPos.inputBottom - viewportPadding
  const opensAbove = prefersAbove
    ? availableAbove >= minimumVisibleMenuHeight || availableAbove >= availableBelow
    : availableBelow < minimumVisibleMenuHeight && availableAbove > availableBelow
  const dropdownTop = caretPos.inputBottom + dropdownOffset
  const dropdownAboveTop = caretPos.inputTop - dropdownOffset
  const dropdownWidth = Math.min(caretPos.inputWidth, Math.max(0, caretPos.viewportWidth - 2 * viewportPadding))
  const dropdownLeft = Math.max(
    viewportPadding,
    Math.min(caretPos.inputLeft, caretPos.viewportWidth - dropdownWidth - viewportPadding),
  )

  return (
    <>
      {ghostText ? (
        <span
          className={styles.ghost}
          aria-hidden="true"
          data-testid="slash-command-hint"
          style={{
            top: `${caretPos.inputTextTop}px`,
            left: `${caretPos.inputTextLeft}px`,
            width: `${caretPos.inputTextWidth}px`,
            font: caretPos.font,
            lineHeight: caretPos.lineHeight,
            letterSpacing: caretPos.letterSpacing,
            wordSpacing: caretPos.wordSpacing,
          }}
        >
          <span className={styles.ghostPrefix}>{currentText}</span>
          {ghostText}
        </span>
      ) : null}
      <VisuallyHidden role="status" aria-live="polite" aria-atomic="true">
        {hasSuggestions
          ? `${suggestions.length} command suggestions available. ${activeSuggestion?.displayKey ?? activeSuggestion?.key ?? ''} selected.`
          : ''}
      </VisuallyHidden>
      {inlineHint ? <VisuallyHidden id={hintId}>Argument: {inlineHint}</VisuallyHidden> : null}
      {hasSuggestions ? (
        <div
          ref={dropdownRef}
          className={clsx(styles.dropdown, opensAbove ? styles.dropdownAbove : styles.dropdownBelow)}
          data-side={opensAbove ? 'outside-top' : 'outside-bottom'}
          style={{
            '--dropdown-top': `${opensAbove ? dropdownAboveTop : dropdownTop}px`,
            top: `${opensAbove ? dropdownAboveTop : dropdownTop}px`,
            left: `${dropdownLeft}px`,
            width: `${dropdownWidth}px`,
          }}
        >
          <ActionList
            ref={setListRef}
            id={listboxId}
            aria-label={label}
            role="listbox"
            variant="inset"
            disableFocusZone
          >
            {suggestions.map((cmd, index) => (
              <ActionList.Item
                key={cmd.key}
                id={`${idPrefix}-option-${instanceId}-${index}`}
                role="option"
                className={cmd.current ? styles.currentItem : undefined}
                active={index === clampedIndex}
                aria-selected={index === clampedIndex}
                aria-current={cmd.current ? 'true' : undefined}
                tabIndex={-1}
                onSelect={() => handleSelect(cmd)}
              >
                <span className={styles.commandLabel}>
                  <span>{cmd.displayKey ?? cmd.key}</span>
                  {cmd.argumentHint ? <span className={styles.commandArgumentHint}> {cmd.argumentHint}</span> : null}
                  {cmd.current ? <VisuallyHidden> (current)</VisuallyHidden> : null}
                </span>
                <ActionList.Description variant="inline" truncate={false} className={styles.commandDescription}>
                  {cmd.description}
                </ActionList.Description>
              </ActionList.Item>
            ))}
          </ActionList>
        </div>
      ) : null}
    </>
  )
}
